import {
  CopyIcon,
  DownloadSimpleIcon,
  EnvelopeSimpleIcon,
  PrinterIcon,
  XIcon,
} from "@phosphor-icons/react";
import { Button } from "@bednbody/ui/components/button";
import { Callout } from "@bednbody/ui/components/callout";
import { NativeSelect, NativeSelectOption } from "@bednbody/ui/components/native-select";
import { SurfaceCard } from "@bednbody/ui/components/surface-card";
import { cn } from "@bednbody/ui/lib/utils";
import { useState } from "react";
import { toast } from "sonner";
import {
  type GiftpassOrder,
  isHandPayable,
  paidBy,
  peso,
  PRINT_LABEL,
  PRINT_STEPS,
  type PrintStatus,
  printStep,
  STATUS_LABEL,
  STATUS_TONE,
} from "./giftpass-orders.model";
import {
  useGiftpassReferrals,
  useSellerPartners,
  useSetOrderSeller,
  useOrderPasses,
  useEmailCodes,
  useProofUrl,
  useSetPrintStatus,
} from "./giftpass-orders.hooks";
import { CancelOrderDialog, MarkPaidDialog } from "./OrderDialogs";
import { printCards } from "./print-cards";

const SHOWN_CODES = 8;

function downloadCodes(order: GiftpassOrder, codes: { code: string; status: string }[]) {
  const csv = ["Code,Status", ...codes.map((c) => `${c.code},${c.status}`)].join("\r\n");
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = `${order.order_code}-codes.csv`;
  link.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-2">
      <h2 className="text-xs font-semibold tracking-wide text-muted-foreground uppercase">
        {title}
      </h2>
      {children}
    </section>
  );
}

export function OrderDetail({
  order,
  isSuper,
  onClose,
}: {
  order: GiftpassOrder;
  isSuper: boolean;
  onClose: () => void;
}) {
  const [dialog, setDialog] = useState<"mark" | "cancel" | null>(null);
  const paid = order.status === "paid";
  const { data: passes = [], isLoading: passesLoading } = useOrderPasses(paid ? order.id : null);
  const { data: referrals = [] } = useGiftpassReferrals();
  const setPrint = useSetPrintStatus();
  const { data: proofUrl } = useProofUrl(order.payment_proof_path);
  const emailCodes = useEmailCodes();
  const handPayable = isHandPayable(order);
  const step = order.print_status as PrintStatus | null;
  const next = printStep(step, 1);
  const back = printStep(step, -1);

  const move = (to: PrintStatus) =>
    setPrint.mutate(
      { orderIds: [order.id], printStatus: to },
      {
        onSuccess: () => toast.success(`${order.order_code}: ${PRINT_LABEL[to]}`),
        onError: (error) => toast.error(error.message),
      },
    );

  return (
    <SurfaceCard
      render={<aside aria-label="Order details" />}
      className="w-full min-w-0 max-w-md flex-[1_1_360px] space-y-6 p-4"
    >
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="font-mono text-lg font-semibold">{order.order_code}</p>
          <p className="text-sm">
            <span className={cn("font-medium", STATUS_TONE[order.status])}>
              {STATUS_LABEL[order.status] ?? order.status}
            </span>
            <span className="text-muted-foreground">
              {" "}
              · {order.source === "manual" ? "Manual" : "Online"}
            </span>
          </p>
        </div>
        <Button variant="ghost" size="icon" aria-label="Close" onClick={onClose}>
          <XIcon />
        </Button>
      </div>

      {handPayable && isSuper ? (
        <Callout tone="warning">
          <p>
            Waiting for the buyer's transfer of <b>{peso(Number(order.amount))}</b>. When the money
            is in, mark it paid and the codes are created.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button onClick={() => setDialog("mark")}>Mark as paid</Button>
            <Button variant="outline" onClick={() => setDialog("cancel")}>
              Cancel order
            </Button>
          </div>
        </Callout>
      ) : null}
      {handPayable && !isSuper ? (
        <Callout>
          Waiting for the bank transfer. Only a Superadmin can mark it paid or cancel it.
        </Callout>
      ) : null}
      {order.status === "pending_payment" ? (
        <Callout tone="info">
          The buyer has a QR Ph code open. PayMongo marks it paid by itself — it can't be marked
          paid by hand.
        </Callout>
      ) : null}

      <Section title="Buyer">
        <p className="font-medium">{order.buyer_name}</p>
        <p className="text-sm text-muted-foreground">
          {order.buyer_phone} · {order.buyer_email}
        </p>
        {order.physical ? (
          <p className="text-sm text-muted-foreground">
            Deliver cards to: {order.delivery_address}
          </p>
        ) : null}
      </Section>

      <SellerSection
        order={order}
        isSuper={isSuper}
        referralName={referrals.find((r) => r.id === order.referral_id)?.name ?? null}
      />

      <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
        <div>
          <dt className="text-xs text-muted-foreground">Package</dt>
          <dd className="font-medium">
            {order.package_name}
            {order.quantity > 1 ? ` × ${order.quantity}` : ""}
          </dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">Passes</dt>
          <dd className="font-medium">{order.total_passes}</dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">Amount</dt>
          <dd className="font-medium">{peso(Number(order.amount))}</dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">Paid by</dt>
          <dd className="font-medium">{paidBy(order) ?? "—"}</dd>
        </div>
        {order.payment_reference ? (
          <div className="col-span-2">
            <dt className="text-xs text-muted-foreground">Reference</dt>
            <dd className="font-mono">{order.payment_reference}</dd>
          </div>
        ) : null}
        {order.payment_proof_path ? (
          <div className="col-span-2">
            <dt className="text-xs text-muted-foreground">Proof of payment</dt>
            <dd>
              {proofUrl ? (
                <a
                  className="text-primary underline-offset-4 hover:underline"
                  href={proofUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  View proof
                </a>
              ) : (
                <span className="text-muted-foreground">Loading…</span>
              )}
            </dd>
          </div>
        ) : null}
      </dl>

      {paid && order.physical && step ? (
        <Section title="Printed cards">
          <ol className="flex gap-1">
            {PRINT_STEPS.map((s, i) => (
              <li
                key={s}
                className={cn(
                  "flex-1 rounded-md px-1 py-1.5 text-center text-sm",
                  s === step
                    ? "bg-primary font-medium text-primary-foreground"
                    : i < PRINT_STEPS.indexOf(step)
                      ? "bg-primary/15"
                      : "bg-muted text-muted-foreground",
                )}
              >
                {PRINT_LABEL[s]}
              </li>
            ))}
          </ol>
          <div className="flex flex-wrap gap-2">
            {next ? (
              <Button disabled={setPrint.isPending} onClick={() => move(next)}>
                Mark {PRINT_LABEL[next].toLowerCase()}
              </Button>
            ) : null}
            {back ? (
              <Button variant="outline" disabled={setPrint.isPending} onClick={() => move(back)}>
                Back to {PRINT_LABEL[back].toLowerCase()}
              </Button>
            ) : null}
            <Button
              variant="outline"
              disabled={passesLoading || passes.length === 0}
              onClick={() => printCards([{ order, codes: passes.map((p) => p.code) }])}
            >
              <PrinterIcon data-icon="inline-start" /> Print cards
            </Button>
          </div>
        </Section>
      ) : null}

      {paid ? (
        <Section title={`Codes · ${order.total_passes}`}>
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              variant="outline"
              disabled={passes.length === 0}
              onClick={() =>
                navigator.clipboard
                  .writeText(passes.map((p) => p.code).join("\n"))
                  .then(() => toast.success(`All ${passes.length} codes copied`))
              }
            >
              <CopyIcon data-icon="inline-start" /> Copy all
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={passes.length === 0}
              onClick={() => downloadCodes(order, passes)}
            >
              <DownloadSimpleIcon data-icon="inline-start" /> Download
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={passes.length === 0 || emailCodes.isPending}
              onClick={() =>
                emailCodes.mutate(order.id, {
                  onSuccess: (result) =>
                    toast.success(`${result.sent} codes emailed to ${order.buyer_email}`),
                  onError: (error) => toast.error(error.message),
                })
              }
            >
              <EnvelopeSimpleIcon data-icon="inline-start" />
              {order.emailed_at ? "Email again" : "Send by email"}
            </Button>
          </div>
          {passesLoading ? <p className="text-sm text-muted-foreground">Loading codes…</p> : null}
          <ul className="grid grid-cols-2 gap-2">
            {passes.slice(0, SHOWN_CODES).map((p) => (
              <li key={p.id} className="rounded-md bg-muted px-2 py-1.5 font-mono text-sm">
                {p.code}
                <span className="text-muted-foreground"> · {p.status}</span>
              </li>
            ))}
          </ul>
          {passes.length > SHOWN_CODES ? (
            <p className="text-sm text-muted-foreground">
              and {passes.length - SHOWN_CODES} more — Copy all or Download gets every code.
            </p>
          ) : null}
          <p className="text-sm text-muted-foreground">
            {order.emailed_at
              ? `Emailed to the buyer ${new Date(order.emailed_at).toLocaleString("en-PH", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}.`
              : "Not emailed yet. Codes go out by themselves once email is set up; until then send them by hand."}
          </p>
        </Section>
      ) : null}

      <MarkPaidDialog
        key={order.id}
        order={order}
        open={dialog === "mark"}
        onOpenChange={(open) => setDialog(open ? "mark" : null)}
      />
      <CancelOrderDialog
        order={order}
        open={dialog === "cancel"}
        onOpenChange={(open) => setDialog(open ? "cancel" : null)}
      />
    </SurfaceCard>
  );
}

/**
 * Who made the sale (0385): a therapist or rider, shown by their partner code
 * as in Luigi's sheets. Older orders may only carry a typed referral name.
 * Super Admin changes it; the change is audited.
 */
function SellerSection({
  order,
  isSuper,
  referralName,
}: {
  order: GiftpassOrder;
  isSuper: boolean;
  referralName: string | null;
}) {
  const { data: partners = [] } = useSellerPartners();
  const setSeller = useSetOrderSeller();
  const current = partners.find((p) => p.id === order.seller_staff_id) ?? null;
  const [draft, setDraft] = useState(order.seller_staff_id ?? "");

  function save() {
    setSeller.mutate(
      { orderId: order.id, staffId: draft || null },
      {
        onSuccess: () => toast.success(draft ? "Seller saved." : "Seller removed."),
        onError: (error) =>
          toast.error(error instanceof Error ? error.message : "Could not save the seller."),
      },
    );
  }

  return (
    <Section title="Seller">
      <p className="text-sm">
        {current ? (
          <>
            <span className="font-mono font-semibold">{current.code}</span>{" "}
            <span className="text-muted-foreground">
              · {current.name} · {current.role === "rider" ? "Rider" : "Therapist"}
            </span>
          </>
        ) : referralName ? (
          <span className="text-muted-foreground">
            Referred by: {referralName} (old entry, no code)
          </span>
        ) : (
          <span className="text-muted-foreground">No seller · company sale</span>
        )}
      </p>
      {isSuper ? (
        <div className="flex flex-wrap items-center gap-2">
          <NativeSelect
            aria-label="Seller"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
          >
            <NativeSelectOption value="">No seller (company sale)</NativeSelectOption>
            {partners.map((p) => (
              <NativeSelectOption key={p.id} value={p.id}>
                {p.code} · {p.name} ({p.role === "rider" ? "Rider" : "Therapist"})
              </NativeSelectOption>
            ))}
          </NativeSelect>
          <Button
            size="sm"
            variant="outline"
            onClick={save}
            loading={setSeller.isPending}
            disabled={draft === (order.seller_staff_id ?? "")}
          >
            Save seller
          </Button>
        </div>
      ) : null}
    </Section>
  );
}
